import { fetchAuthSession } from 'aws-amplify/auth'
import { FILE_API_BASE } from '../components/constants/constants'

const authHeaders = async () => {
  const { tokens } = await fetchAuthSession()
  const idToken = tokens?.idToken?.toString()
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${idToken}`,
  }
}

const parseError = async response => {
  let message = `Request failed (${response.status})`
  try {
    const data = await response.json()
    if (data?.message) message = data.message
  } catch {
    /* keep default */
  }
  return new Error(message)
}

export const listFiles = async ({ bucket, prefix = '', continuationToken }) => {
  const params = new URLSearchParams({ bucket, prefix })
  if (continuationToken) params.set('continuationToken', continuationToken)

  const response = await fetch(`${FILE_API_BASE}/list?${params.toString()}`, {
    headers: await authHeaders(),
  })
  if (!response.ok) throw await parseError(response)
  return response.json()
}

export const getBatchUrls = async items => {
  const response = await fetch(`${FILE_API_BASE}/batch-urls`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({
      items: items.map(({ bucket, key }) => ({ bucket, key })),
    }),
  })
  if (!response.ok) throw await parseError(response)
  const data = await response.json()
  return data.items
}

export const createFolder = async ({ bucket, prefix = '', folderName }) => {
  const response = await fetch(`${FILE_API_BASE}/folder`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ bucket, prefix, folderName }),
  })
  if (!response.ok) throw await parseError(response)
  return response.json()
}

export const shareFilesByEmail = async ({ emails, items, message }) => {
  const response = await fetch(`${FILE_API_BASE}/share`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({
      emails,
      message,
      items: items.map(({ bucket, key, name }) => ({ bucket, key, name })),
    }),
  })
  if (!response.ok) throw await parseError(response)
  return response.json()
}

const DELETE_BATCH_LIMIT = 50 // backend /delete hard cap per request

export const deleteFiles = async items => {
  const deleted = []
  const errors = []
  for (let i = 0; i < items.length; i += DELETE_BATCH_LIMIT) {
    const batch = items.slice(i, i + DELETE_BATCH_LIMIT)
    const response = await fetch(`${FILE_API_BASE}/delete`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({
        items: batch.map(({ bucket, key }) => ({ bucket, key })),
      }),
    })
    if (!response.ok) throw await parseError(response)
    const data = await response.json()
    deleted.push(...(data.deleted || []))
    errors.push(...(data.errors || []))
  }
  return { deleted, errors }
}

export const getUploadUrl = async ({ bucket, prefix = '', fileName, contentType }) => {
  const response = await fetch(`${FILE_API_BASE}/upload-url`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ bucket, prefix, fileName, contentType }),
  })
  if (!response.ok) throw await parseError(response)
  return response.json()
}

/** Every grant record across all folders (super admin only) — Settings "File Sharing" overview. */
export const getAllFolderGrants = async () => {
  const response = await fetch(`${FILE_API_BASE}/grants?all=true`, {
    headers: await authHeaders(),
  })
  if (!response.ok) throw await parseError(response)
  return response.json()
}

export const getFolderGrants = async prefix => {
  const params = new URLSearchParams({ prefix })
  const response = await fetch(`${FILE_API_BASE}/grants?${params.toString()}`, {
    headers: await authHeaders(),
  })
  if (!response.ok) throw await parseError(response)
  return response.json()
}

export const updateFolderGrants = async ({ prefix, add = [], remove = [], admins = [], access }) => {
  const response = await fetch(`${FILE_API_BASE}/grants`, {
    method: 'POST',
    headers: await authHeaders(),
    // access ("read" | "write") applies to the `add` emails; backend defaults to write
    body: JSON.stringify({ prefix, add, remove, admins, ...(access ? { access } : {}) }),
  })
  if (!response.ok) throw await parseError(response)
  return response.json()
}

export const getFileAdmins = async () => {
  const response = await fetch(`${FILE_API_BASE}/admins`, {
    headers: await authHeaders(),
  })
  if (!response.ok) throw await parseError(response)
  return response.json()
}

export const getMyGrants = async () => {
  const response = await fetch(`${FILE_API_BASE}/my-grants`, {
    headers: await authHeaders(),
  })
  if (!response.ok) throw await parseError(response)
  return response.json()
}

export const uploadFileToUrl = async (file, signedUrl) => {
  const response = await fetch(signedUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  })
  if (!response.ok) throw new Error(`Upload failed (${response.status})`)
}

export const downloadFromUrl = async (url, fileName) => {
  try {
    const response = await fetch(url)
    if (!response.ok) throw new Error('fetch failed')
    const blob = await response.blob()
    const blobUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(blobUrl)
  } catch {
    // CORS or network fallback — let the browser handle it in a new tab
    window.open(url, '_blank', 'noopener')
  }
}

export const formatFileSize = bytes => {
  if (bytes === 0) return '0 B'
  if (!bytes && bytes !== 0) return 'N/A'
  const units = ['B', 'KB', 'MB', 'GB']
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / 1024 ** i
  return `${value >= 10 || i === 0 ? Math.round(value) : value.toFixed(1)} ${units[i]}`
}

export const formatFileDate = dateString => {
  if (!dateString) return 'N/A'
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
